import { Core } from "@faire/design-tokens";
import { SafelySetInnerHTML } from "@faire/web--source/common/SafelySetInnerHTML";
import { Button } from "@faire/web--source/slate/Button";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import articlePage from "@faire/web-api--source/routes/www/support/articles/articleId";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";

import { IRoundSearchInputProps, ISearchResult } from "./RoundSearchInput";
import { highlightText } from "./utils/highlightText";

interface ISearchResultProps extends IRoundSearchInputProps {
  searchQuery: string;
  searchResult: readonly ISearchResult[];
  resetSearch: () => void;
  selected: number;
  setSelected: (value: number) => void;
  goToSearchPage: () => void;
  hasSearchResult: boolean;
  isSearchInputAtHeader: boolean;
}

const MAX_SEARCH_RESULT = 6;

export const SearchResult: React.FC<ISearchResultProps> = ({
  searchQuery,
  searchResult,
  navigatedFrom,
  sectionFrom,
  resetSearch,
  selected,
  setSelected,
  goToSearchPage,
  hasSearchResult,
  isSearchInputAtHeader,
}) => {
  const history = useHistory();
  return (
    <SearchResultWrapper
      $isSearchInputAtHeader={isSearchInputAtHeader}
      data-test-id="search-result"
    >
      {searchResult.map((article, index) => {
        if (index >= MAX_SEARCH_RESULT) {
          return null;
        }
        return (
          <li key={article.id}>
            <Link
              to={{
                pathname: articlePage(article.id),
                state: {
                  navigatedFrom,
                  articleTitle: article.title,
                  pageFrom: history.location?.pathname,
                  sectionFrom,
                },
              }}
              onClick={resetSearch}
              className={selected === index ? "search-item-selected" : ""}
              onFocus={() => setSelected(index)}
            >
              <SafelySetInnerHTML
                html={highlightText(article.title, searchQuery)}
                tag="h2"
              />
            </Link>
          </li>
        );
      })}
      {hasSearchResult ? (
        <ButtonWrapper variant="tertiary" onClick={goToSearchPage}>
          <StrictLocalMsg
            defaultMessage="See all results"
            description={{
              text: "This is a button to see all search results",
            }}
          />
        </ButtonWrapper>
      ) : null}
    </SearchResultWrapper>
  );
};

const SearchResultWrapper = styled.ul<{ $isSearchInputAtHeader: boolean }>`
  background: ${Core.surface.inverse};
  position: absolute;
  width: 100%;
  border: 1px solid ${Core.border.muted};
  z-index: 2;
  text-align: left;
  max-height: 420px;
  ${(props) => (props.$isSearchInputAtHeader ? "top: 46px;" : "top: 55px;")}
  border-radius: 8px;
  li {
    a {
      display: block;
      padding: ${getSpacing("2x")};
      &.search-item-selected,
      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
    h2 {
      ${TypographyStyles({})}
    }
    p {
      ${TypographyStyles({ color: Core.text.subdued })}
      em {
        font-weight: bold;
        font-style: normal;
      }
    }
  }
`;

const ButtonWrapper = styled(Button).attrs({ variant: "tertiary" })`
  padding: ${getSpacing("1x", "2x", "2x", "2x")};
`;
